/**
 * CSS do widget, gerado a partir do `appearance`.
 *
 * Duas coisas que este arquivo carrega nas costas:
 *
 * 1. **Isolamento.** Todo o CSS vive dentro do Shadow DOM e o container começa com `all: initial`.
 *    Sem isso, um `* { box-sizing: content-box }` ou um `button { text-transform: uppercase }` do
 *    site host desmontaria o widget — e o contrário também: nada daqui escapa para a página.
 *
 * 2. **Cores revalidadas.** As cores vêm da config, que chega pela rede. O schema já as valida no
 *    servidor, mas aqui elas são interpoladas dentro de uma string de CSS, então passar de novo
 *    pela mesma regra é barato e fecha a porta para injeção de CSS caso a config venha de outro
 *    caminho (preview do painel, config montada à mão em teste).
 */

import { resolveChatSurface, type Appearance, type ChatSurface } from '@merlin/flow-engine';
import { doodleDataUri, DOODLE_TILE_PX } from './doodle.js';

const SAFE_COLOR = /^(#[0-9a-fA-F]{3,8}|rgba?\([\d.\s,%/]+\)|[a-zA-Z]{3,24})$/;

export function safeColor(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  const trimmed = value.trim();
  return SAFE_COLOR.test(trimmed) ? trimmed : fallback;
}

function clampOffset(value: number | undefined): number {
  if (!Number.isFinite(value)) return 24;
  return Math.min(Math.max(Math.trunc(value as number), 0), 400);
}

/** Altura do botão flutuante e o espaçamento entre ele e a janela. Usados na conta da altura máxima. */
const LAUNCHER_SIZE = 60;
const STACK_GAP = 12;
/** Respiro acima da janela, para ela não encostar no topo da tela. */
const TOP_BREATHING_ROOM = 24;
/** Teto de margem lateral. Acima disso, tela estreita não conseguiria honrar os dois lados. */
const MAX_SIDE_MARGIN = 24;

/**
 * Margem que a janela de chat mantém de **cada** lado da tela.
 *
 * Existe porque a largura e a âncora precisam concordar. A janela é ancorada a `offsetX` de uma
 * borda; se a largura fosse limitada por outro número, sobraria menos espaço do outro lado — era
 * justamente o defeito: `- 32px` na largura (supondo 16px por lado) contra `right: 24px` na âncora
 * deixava 8px à esquerda e 24px à direita.
 *
 * O teto protege configuração extrema: o schema aceita `offsetX` até 400px, e numa tela de 390px não
 * há como respeitar isso dos dois lados.
 */
export function panelSideMargin(offsetX: number): number {
  return Math.min(clampOffset(offsetX), MAX_SIDE_MARGIN);
}

/**
 * Espaço vertical que a janela precisa deixar livre: o afastamento configurado e um respiro no topo.
 *
 * **O botão flutuante não entra mais nesta conta.** Ele some enquanto a conversa está aberta, então
 * a janela ocupa o lugar dele em vez de se empilhar acima — são 72px a mais de conversa (os 60 do
 * botão e os 12 do espaçamento) nas telas em que a altura é limitada pelo viewport.
 *
 * `LAUNCHER_SIZE` e `STACK_GAP` continuam existindo porque governam o botão em si e o espaçamento
 * do `.root`; só saíram desta conta.
 */
export function panelBottomReserve(offsetY: number): number {
  return clampOffset(offsetY) + TOP_BREATHING_ROOM;
}

/** Onde ficam os assets do widget, relativo à origem que serviu o script. */
const ASSET_PATH = '/widget/assets/';

/**
 * Camadas de `background` da área de conversa.
 *
 * O ladrilho do WhatsApp tem traço branco com alpha de até 90% — direto no fundo ele sairia com linhas
 * quase brancas. A intensidade sai de uma **camada de véu**: a primeira camada listada em
 * `background-image` fica em cima, então um gradiente de cor sólida (o canvas com alpha) por cima do
 * ladrilho deixa passar só uma fração do branco. Com véu a 92,5%, sobra ~6,7%.
 *
 * O véu resolve o que um pseudo-elemento resolveria, sem pseudo-elemento: `::before` sobre uma área
 * com rolagem exigiria `z-index` e brigaria com as mensagens.
 *
 * Se a imagem não carregar — CSP do site host, rede fora — o navegador simplesmente não pinta a
 * camada, e fica o `background-color`. Degrada sozinho.
 */
export function backgroundLayers(surface: ChatSurface, assetBase: string): string {
  const pattern = surface.pattern;
  if (!pattern) return '';

  if (pattern.kind === 'doodle') {
    return (
      `\n  background-image: ${doodleDataUri(safeColor(pattern.color, '#ffffff'), pattern.opacity)};` +
      `\n  background-repeat: repeat;` +
      `\n  background-size: ${DOODLE_TILE_PX}px ${DOODLE_TILE_PX}px;`
    );
  }

  // Sem base de assets não há URL válida a emitir: melhor ficar só o canvas que apontar para o nada.
  const base = assetBase.replace(/\/$/, '');
  if (!base || !/^https?:\/\//i.test(base)) return '';
  const veil = safeColor(pattern.veil, surface.canvas);
  const url = `${base}${ASSET_PATH}${encodeURIComponent(pattern.file)}`;

  return (
    `\n  background-image: linear-gradient(${veil}, ${veil}), url("${url}");` +
    `\n  background-repeat: repeat;`
  );
}

export function buildStyles(appearance: Appearance, assetBase = ''): string {
  /*
   * O tema da conversa. Ausente cai no padrão — é assim que webchat publicado antes deste campo herda
   * o fundo novo sem republicar. Ver `resolveChatSurface`.
   *
   * O tema cobre canvas **e** moldura (compositor, rodapé, input). Só o canvas deixaria uma faixa
   * branca embaixo do fundo escuro, com cara de dois aplicativos costurados.
   */
  const surface = resolveChatSurface(appearance.chatBackground);
  const canvas = safeColor(surface.canvas, '#ffffff');
  const chrome = safeColor(surface.surface, '#ffffff');
  const chromeBorder = safeColor(surface.surfaceBorder, '#e5e7eb');
  const inputBg = safeColor(surface.inputBg, '#ffffff');
  const inputBorder = safeColor(surface.inputBorder, '#d1d5db');
  const muted = safeColor(surface.muted, '#94a3b8');
  const danger = safeColor(surface.danger, '#b91c1c');
  const textura = backgroundLayers(surface, assetBase);

  const primary = safeColor(appearance.primaryColor, '#25D366');
  const bubble = safeColor(appearance.bubbleColor, '#f1f5f9');
  const text = safeColor(appearance.textColor, '#0f172a');

  /*
   * Bolha enviada. Nos temas do WhatsApp ela vem do tema, porque la' o balao enviado e' um verde
   * claro fixo — pinta-lo com a cor da marca era o que mais destoava do aplicativo. Com
   * `outgoing: null` (tema branco liso) cai no comportamento antigo, a cor da marca com texto branco.
   */
  const outBubble = safeColor(surface.outgoing?.bubble, primary);
  const outText = safeColor(surface.outgoing?.text, '#ffffff');
  const outMeta = safeColor(surface.outgoing?.meta, 'rgba(255,255,255,0.75)');
  const metaColor = safeColor(surface.meta, 'rgba(15,23,42,0.45)');
  const tickColor = safeColor(surface.tick, '#53bdeb');

  /*
   * Cabecalho e circulo do glifo. Onde o tema nao opina (`null`), a marca continua mandando no
   * cabecalho — e' o lugar do widget onde a identidade do cliente mais aparece. So' o tema claro
   * define o par verde-escuro/verde-vivo do icone do aplicativo.
   */
  const headerBg = safeColor(surface.header ?? undefined, primary);
  const avatarBg = safeColor(surface.avatar ?? undefined, 'rgba(255,255,255,0.22)');
  const offsetX = clampOffset(appearance.offsetX);
  const offsetY = clampOffset(appearance.offsetY);
  const sideMargin = panelSideMargin(offsetX);
  const bottomReserve = panelBottomReserve(offsetY);

  const vertical = appearance.position.startsWith('top') ? 'top' : 'bottom';
  const horizontal = appearance.position.endsWith('left') ? 'left' : 'right';
  const panelOrigin = `${vertical === 'top' ? 'top' : 'bottom'} ${horizontal}`;

  return `
:host { all: initial; }
* { box-sizing: border-box; }

.root {
  position: fixed;
  ${vertical}: ${offsetY}px;
  ${horizontal}: ${offsetX}px;
  z-index: 2147483000;
  display: flex;
  /*
   * A ordem no DOM e' [janela, botao]. Ancorado embaixo, a janela precisa ficar acima do botao —
   * ou seja, a ordem visual e' a mesma do DOM, o que pede "column". Ancorado no topo, o botao vem
   * primeiro e a janela desce, o que pede "column-reverse". Invertido, o botao flutua acima da
   * conversa. (Sem acento grave neste comentario: ele vive dentro de um template literal.)
   */
  flex-direction: ${vertical === 'top' ? 'column-reverse' : 'column'};
  align-items: ${horizontal === 'left' ? 'flex-start' : 'flex-end'};
  gap: 12px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 15px;
  line-height: 1.45;
  color: ${text};
}

.launcher {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  padding: 0;
  cursor: pointer;
  background: ${primary};
  color: #fff;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.25);
  display: grid;
  place-items: center;
  overflow: hidden;
  transition: transform 120ms ease, box-shadow 120ms ease;
}
.launcher:hover { transform: scale(1.05); }
.launcher:focus-visible { outline: 3px solid ${primary}; outline-offset: 3px; }
.launcher img { width: 100%; height: 100%; object-fit: cover; display: block; }
.launcher svg { width: 32px; height: 32px; display: block; }

.panel {
  /*
   * As propriedades --merlin-viewport-width/height dizem que tamanho de tela o widget deve assumir.
   * Num site real ninguem as define, e vale 100vw/100vh — o correto la'.
   *
   * Quem as escreve e' o **proprio widget**, em publicarViewport (ui/widget.ts), sempre que houver um
   * "container" explicito: ele ja' media essa caixa para decidir o modo compacto, e o CSS estava
   * jogando a medida fora. Propriedade customizada atravessa a fronteira do Shadow DOM, entao basta
   * escrever no host. (Sem acento grave neste comentario: ele vive dentro de um template literal.)
   *
   * Sao duas contas diferentes e as duas dependem disto. A largura era o caso obvio, do preview de
   * celular: num palco de 320px, 100vw continuaria valendo a janela do navegador e a janela de chat
   * sairia com 370px, cortada. A **altura** era o caso silencioso, do preview de computador: o palco
   * e' uns 110px mais baixo que a janela (o cabecalho do painel e o respiro de cada lado), e com
   * 100vh a janela de chat, ancorada embaixo, transbordava por cima — sumia o cabecalho dela.
   *
   * A margem descontada e' o dobro de panelSideMargin(offsetX), e nao um valor fixo: a janela e'
   * ancorada a offsetX de uma borda, entao limitar a largura por outro numero deixaria os dois lados
   * com folgas diferentes. Ver panelSideMargin para a conta.
   */
  width: min(370px, calc(var(--merlin-viewport-width, 100vw) - ${sideMargin * 2}px));
  /*
   * Altura **fixa**, nao teto. Com so um max-height a janela crescia junto com o conteudo: no comeco
   * da conversa era uma caixa baixa e ia esticando a cada mensagem. Interface de celular tem altura
   * constante, e e' o que faz a janela parecer um aparelho em vez de um balao que incha.
   */
  height: min(640px, calc(var(--merlin-viewport-height, 100vh) - ${bottomReserve}px));
  background: ${chrome};
  border-radius: 16px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.28);
  display: none;
  flex-direction: column;
  overflow: hidden;
  transform-origin: ${panelOrigin};
  animation: pop 140ms ease-out;
}
.panel[data-open="true"] { display: flex; }

/*
 * Com a conversa aberta, o botao flutuante sai de cena e a janela ocupa o lugar dele.
 *
 * Vale em qualquer largura, nao so' em tela cheia: empilhado acima do botao, a janela desperdicava
 * 72px de altura (os 60 do botao mais os 12 de espacamento) e o botao ficava ali sem funcao, ja' que
 * o fechar esta' no cabecalho e o Escape tambem fecha. Ver panelBottomReserve, que deixou de
 * descontar esse espaco.
 */
.root[data-open="true"] .launcher { display: none; }

@keyframes pop {
  from { opacity: 0; transform: translateY(${vertical === 'top' ? '-8px' : '8px'}) scale(0.98); }
  to { opacity: 1; transform: none; }
}

.header {
  background: ${headerBg};
  color: #fff;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
}
.header .avatar {
  width: 38px; height: 38px; border-radius: 50%;
  background: ${avatarBg};
  display: grid; place-items: center; overflow: hidden; flex: 0 0 auto;
}
.header .avatar img { width: 100%; height: 100%; object-fit: cover; }
.header .titles { flex: 1 1 auto; min-width: 0; }
.header .title { font-weight: 600; font-size: 15px; display: block; }
.header .subtitle { font-size: 12.5px; opacity: 0.9; display: block; }
.header .close {
  background: transparent; border: none; color: #fff; cursor: pointer;
  font-size: 22px; line-height: 1; padding: 4px 6px; border-radius: 6px;
}
.header .close:hover { background: rgba(255,255,255,0.18); }

/*
 * O background-attachment fica no padrao ("scroll"): a textura acompanha a caixa, nao o conteudo
 * rolado, entao ela nao desliza atras das mensagens — que e' como o WhatsApp se comporta.
 * (Sem acento grave aqui: este comentario vive dentro de um template literal.)
 */
.messages {
  flex: 1 1 auto;
  /*
   * Sem isto, um filho flex se recusa a encolher abaixo do tamanho do proprio conteudo. Com a altura
   * do painel fixa, a lista de mensagens empurraria o compositor para fora da janela em vez de rolar.
   */
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${canvas};${textura}
  scroll-behavior: smooth;
}

.bubble {
  position: relative;
  max-width: 85%;
  padding: 6px 9px 8px;
  border-radius: 7.5px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  box-shadow: 0 1px 0.5px rgba(11, 20, 26, 0.13);
}
.bubble[data-role="bot"] { background: ${bubble}; color: ${text}; align-self: flex-start; }
.bubble[data-role="visitor"] { background: ${outBubble}; color: ${outText}; align-self: flex-end; }
.bubble[data-role="system"] {
  align-self: center; background: #fff7ed; color: #9a3412;
  border: 1px solid #fed7aa; font-size: 13px; max-width: 100%; box-shadow: none;
}

/*
 * Rabicho: triangulo de 8px no canto de cima, so na **primeira** bolha de uma sequencia do mesmo
 * lado — e' assim que o WhatsApp agrupa. Cabe nos 16px de padding do .messages, entao nao e'
 * recortado nem cria barra de rolagem horizontal.
 */
.bubble[data-first="true"] { border-top-left-radius: 0; }
.bubble[data-role="visitor"][data-first="true"] { border-top-left-radius: 7.5px; border-top-right-radius: 0; }

.bubble[data-first="true"]::before {
  content: "";
  position: absolute;
  top: 0;
  width: 0;
  height: 0;
  border-top: 8px solid ${bubble};
}
.bubble[data-role="bot"][data-first="true"]::before {
  left: -8px;
  border-left: 8px solid transparent;
}
.bubble[data-role="visitor"][data-first="true"]::before {
  right: -8px;
  border-top-color: ${outBubble};
  border-right: 8px solid transparent;
}
.bubble[data-role="system"][data-first="true"]::before { display: none; }
.bubble[data-role="system"][data-first="true"] { border-radius: 7.5px; }

/*
 * Horario dentro da bolha. O float e' o que reproduz o comportamento do WhatsApp de graca: em
 * mensagem curta ele fica na mesma linha do texto, em mensagem longa desce sozinho para a linha de
 * baixo, sempre encostado a' direita.
 */
.meta {
  float: right;
  margin: 6px -3px -3px 8px;
  font-size: 11px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: ${metaColor};
  user-select: none;
}
.bubble[data-role="visitor"] .meta { color: ${outMeta}; }
.meta svg { width: 15px; height: 15px; display: block; color: ${tickColor}; }

.typing { display: inline-flex; gap: 4px; align-items: center; }
.typing span {
  width: 6px; height: 6px; border-radius: 50%; background: currentColor; opacity: 0.45;
  animation: blink 1s infinite ease-in-out;
}
.typing span:nth-child(2) { animation-delay: 0.15s; }
.typing span:nth-child(3) { animation-delay: 0.3s; }
@keyframes blink { 0%, 60%, 100% { opacity: 0.25; } 30% { opacity: 0.9; } }

.composer { flex: 0 0 auto; border-top: 1px solid ${chromeBorder}; padding: 12px; background: ${chrome}; }
.options { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
.options:empty { display: none; margin: 0; }
.option {
  background: ${chrome}; border: 1.5px solid ${primary}; color: ${primary};
  border-radius: 999px; padding: 7px 14px; cursor: pointer; font-size: 14px; font-family: inherit;
}
.option:hover { background: ${primary}; color: #fff; }
.option:focus-visible { outline: 2px solid ${text}; outline-offset: 2px; }

.form { display: flex; gap: 8px; }
.form[hidden] { display: none; }
.input {
  flex: 1 1 auto; min-width: 0; border: 1px solid ${inputBorder}; border-radius: 10px;
  padding: 10px 12px; font-size: 15px; font-family: inherit; color: ${text}; background: ${inputBg};
}
/* Sem isto, o placeholder padrao do navegador (cinza escuro) desaparece no input do tema escuro. */

.input::placeholder { color: ${muted}; opacity: 1; }
.input:focus { outline: none; border-color: ${primary}; box-shadow: 0 0 0 3px rgba(0,0,0,0.05); }
.send {
  border: none; background: ${primary}; color: #fff; border-radius: 10px;
  padding: 0 16px; cursor: pointer; font-size: 15px; font-family: inherit; font-weight: 600;
}
.send:disabled { opacity: 0.55; cursor: default; }

.hint { font-size: 12.5px; color: ${danger}; margin-top: 6px; min-height: 0; }
.hint:empty { display: none; }

.footer {
  font-size: 11.5px; color: ${muted}; text-align: center; padding: 0 12px 10px; background: ${chrome};
}
.footer[hidden] { display: none; }

/*
 * Modo compacto: em tela estreita a conversa ocupa tudo, como o app do WhatsApp.
 *
 * O gatilho e' o atributo data-compact, posto pelo JS a partir da viewport **efetiva**, e nao uma
 * media query. Motivo: o preview do painel simula um celular num palco de 390px dentro de uma janela
 * de desktop larga; media query leria a janela real e o fullscreen so existiria em producao — o pior
 * tipo de comportamento, o que nao da' para ver antes de publicar. Ver viewportSize em ui/widget.ts.
 */
/*
 * Tudo aqui exige **os dois** atributos, compacto e aberto. Com a conversa fechada, um inset zero
 * faria a raiz cobrir a tela inteira e engolir os cliques do site do cliente, e esconder o launcher
 * deixaria o visitante sem como abrir o chat. Foi o defeito da primeira versao deste bloco.
 *
 * A altura sai **so'** do inset zero, sem nenhuma unidade de viewport. Duas tentativas anteriores
 * falharam por misturar caixas: a primeira punha 100dvh no painel enquanto a raiz usava inset, e a
 * segunda punha 100dvh na propria raiz. Nos dois casos a caixa ficava menor que a tela — em fixed,
 * inset zero se dimensiona pelo bloco conteiner inicial, que no Android Chrome e' o viewport
 * **grande**, enquanto dvh com a barra de endereco a' mostra e' uns 60 a 90px menor. A diferenca
 * aparecia como uma faixa da pagina do cliente embaixo da conversa.
 *
 * Consequencia assumida: onde o bloco conteiner inicial e' o viewport grande, a base do painel pode
 * ficar atras da barra do navegador enquanto ela esta' visivel. As mensagens rolam e o compositor ja'
 * reserva a area segura, entao o custo e' pequeno perto de deixar o site do cliente aparecendo.
 * (Sem acento grave aqui: este comentario vive dentro de um template literal.)
 */
.root[data-compact="true"][data-open="true"] {
  inset: 0;
  gap: 0;
  align-items: stretch;
}

/*
 * O painel preenche a **raiz**, e nao uma unidade de viewport propria. Defeito real que isto
 * conserta: a raiz se dimensiona por inset zero (bloco conteiner inicial) e o painel usava 100dvh,
 * o viewport dinamico, que encolhe enquanto a barra de endereco esta' visivel. As duas caixas
 * mediam coisas diferentes, e sobrava uma faixa de uns 60px embaixo mostrando a pagina do cliente.
 *
 * Com height de 100% existe uma medida so. Quem acompanha a barra do navegador e' a raiz, logo
 * abaixo — e o painel vai junto de graca.
 * (Sem acento grave neste comentario: ele vive dentro de um template literal.)
 */
.root[data-compact="true"][data-open="true"] .panel {
  width: 100%;
  height: 100%;
  max-width: none;
  border-radius: 0;
  box-shadow: none;
  animation: subir 180ms ease-out;
}


/* Barra de gestos do iPhone comeria o campo de texto sem isto. */
.root[data-compact="true"][data-open="true"] .composer {
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
}
.root[data-compact="true"][data-open="true"] .header {
  padding-top: calc(14px + env(safe-area-inset-top, 0px));
}

@keyframes subir {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  .panel { animation: none; }
  .launcher { transition: none; }
  .typing span { animation: none; }
}
`;
}
